/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable no-underscore-dangle */
import { NetworkService } from './../network.service';
import { SoundsService } from './../sounds.service';
import { Settings } from './settings.type';
import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { take, tap, timeout } from 'rxjs/operators';
import { Preferences } from '@capacitor/preferences';
import { DEFAULT_SETTINGS, DEFAULT_SHOW_SETTINGS } from '../constants';
import { GetDate } from '../date-getter';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export abstract class SettingsService {
  public static settings: Settings;
  public _settingData = new Subject<Settings>();
  public _appState = new Subject<string>();

  bluetooth = true;
  wifi = false;
  shiftSub: Subscription;
  userSub: Subscription;
  networkSub: Subscription;

  networkType: string;
  userId: string;
  collectionStartTime: Date;

  get settingData() {
    return this._settingData.asObservable();
  }

  get appState() {
    return this._appState.asObservable();
  }

  constructor(
    private http: HttpClient,
    private soundsService: SoundsService,
    private networkService: NetworkService,
    ) {
    /*
      DEFAULT SETTINGS
    */
    let defaultSettings = DEFAULT_SETTINGS;
    let defaultShowSettings = DEFAULT_SHOW_SETTINGS;

    const calibrationSections = [  //Default calibration sections
      { name: 'Center', id: 'center', checked: true, bounds: { x: 4, y: 4, width: 8, height: 8 } },
      { name: 'Left', id: 'left', checked: true, bounds: { x: 0, y: 0, width: 6, height: 16 } },
      { name: 'Right', id: 'right', checked: true, bounds: { x: 10, y: 0, width: 6, height: 16 } },
      { name: 'Front', id: 'front', checked: true, bounds: { x: 0, y: 0, width: 16, height: 6 } },
      { name: 'Back', id: 'back', checked: true, bounds: { x: 0, y: 7, width: 16, height: 9 } },
    ];

    this.networkSub = this.networkService.networkTypeObs.subscribe((networkType) => {
      this.networkType = networkType;
    });

    const completeSoundPath = this.soundsService.defaultSoundPath();
    const notificationSoundPath = this.soundsService.defaultNotificationPath();
    const serverURL = '';

    this.get('authData').then((authData) => {
      try {
        authData = JSON.parse(authData.value);

        SettingsService.settings.userId = authData.id;
        this._settingData.next(SettingsService.settings);
      } catch {
      }
    });

    this.get('collection_start').then((collectionStart) => {
      let collectionStartString = GetDate.getDate().getTime().toString();
      try {
        if (!collectionStart || !collectionStart.value) {
          // default value
          this.set('collection_start', collectionStartString);
        } else {
            collectionStartString = collectionStart.value;
        }
        this.collectionStartTime = new Date(+collectionStartString);
        SettingsService.settings.collectionStartTime = new Date(+collectionStartString);
      } catch {
      }
    });

    const booleanSettings = [
      {name: 'soundOnComplete', value: defaultSettings.soundOnComplete},
      {name: 'soundOnNotification', value: defaultSettings.soundOnNotification},
      {name: 'soundNotifications', value: defaultSettings.soundNotifications},
      {name: 'dailyNotifications', value: defaultSettings.dailyNotifications},
      {name: 'showDetails', value: defaultSettings.showDetails},
      {name: 'showPPIDetails', value: defaultSettings.showPPIDetails},
      {name: 'showProgress', value: defaultSettings.showProgress},
      {name: 'showNotifications', value: defaultSettings.showNotifications},
      {name: 'uploadContinuosMaps', value: defaultSettings.uploadContinuosMaps},
      {name: 'uploadContinuosMetrics', value: defaultSettings.uploadContinuosMetrics},
      {name: 'showShiftTimer', value: defaultSettings.showShiftTimer},
      {name: 'detectShifts', value: defaultSettings.detectShifts},
      {name: 'performCalibration', value: defaultSettings.performCalibration},
      {name: 'isHeadless', value: defaultSettings.isHeadless},
    ];

    for (const boolSetting of booleanSettings) {
      this.get(boolSetting.name).then((bool) => {
        if (!bool || !bool.value) {
          // default value
          this.set(boolSetting.name, boolSetting.value);
        } else {
          boolSetting.value = bool.value === 'true';
        }
        SettingsService.settings[boolSetting.name] = boolSetting.value;
        this._settingData.next(SettingsService.settings);
      });
    }

    const stringSettings = [
      {name: 'serverURL', value: serverURL},
      {name: 'completeSoundPath', value: completeSoundPath},
      {name: 'notificationSoundPath', value: notificationSoundPath},
      {name: 'inputRotation', value: defaultSettings.inputRotation},
      {name: 'selectedTheme', value: defaultSettings.selectedTheme},
      {name: 'selectedMapColor', value: defaultSettings.selectedMapColor},
      {name: 'pressureReliefTechnique', value: defaultSettings.pressureReliefTechnique},
    ];

    for (const stringSetting of stringSettings) {
      this.get(stringSetting.name).then((stringSet) => {
        if (!stringSet || !stringSet.value) {
          // default value
          this.set(stringSetting.name, stringSetting.value);
        } else {
          stringSetting.value = stringSet.value.toString();
        }
        SettingsService.settings[stringSetting.name] = stringSetting.value;
        this._settingData.next(SettingsService.settings);
      });
    }

    const timeSettings = [
      {name: 'dailyStartTime', hour: defaultSettings.dafaultStartTimeHour, minute: defaultSettings.dafaultStartTimeMinute, value: null},
      {name: 'dailyStopTime', hour: defaultSettings.dafaultStopTimeHour, minute: defaultSettings.dafaultStopTimeMinute, value: null},
    ];
    for (const timeSetting of timeSettings) {
      this.get(timeSetting.name).then((time) => {
        const today = GetDate.getDate();
        if (!time || !time.value) {
          // default value
          const defaultTime = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            timeSetting.hour,
            timeSetting.minute
          );
          this.set(timeSetting.name, defaultTime.toISOString());
        } else {
          timeSetting.hour = new Date(time.value).getHours();
          timeSetting.minute = new Date(time.value).getMinutes();

          if (GetDate.automaticSkewDate(new Date(time.value)) <= today) {
            // date is not today reset start time to be todays date
            const startTime = new Date(
              today.getFullYear(),
              today.getMonth(),
              today.getDate(),
              timeSetting.hour,
              timeSetting.minute
            );
            timeSetting.value = startTime;
            this.set(timeSetting.name, startTime.toISOString());
          }
        }

        SettingsService.settings[timeSetting.name] = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          timeSetting.hour,
          timeSetting.minute
        );
        this._settingData.next(SettingsService.settings);
      });
    }

    const numberSettings = [
      {name: 'shiftInterval', value: defaultSettings.shiftInterval},
      {name: 'shiftDuration', value: defaultSettings.shiftDuration},
      {name: 'globalAlertTriggerPct', value: defaultSettings.globalAlertTriggerPct},
      {name: 'globalAlertTriggerThreshold', value: defaultSettings.globalAlertTriggerThreshold},
      {name: 'localAlertTriggerPpi', value: defaultSettings.localAlertTriggerPpi},
      {name: 'localAlertTimeThreshold', value: defaultSettings.localAlertTimeThreshold},
      {name: 'shiftDailyGoalCount', value: defaultSettings.shiftDailyGoalCount},
      {name: 'uploadIntervalMinutes', value: defaultSettings.uploadIntervalMinutes},
      {name: 'sampleRate', value: defaultSettings.sampleRate},
      {name: 'shiftsPerHour', value: defaultSettings.shiftsPerHour},
      {name: 'detectionDuration', value: defaultSettings.detectionDuration},
      {name: 'detectionPPIThreshold', value: defaultSettings.detectionPPIThreshold},
      {name: 'detectionPPICount', value: defaultSettings.detectionPPICount},
    ];

    for (const numberSetting of numberSettings) {
      this.get(numberSetting.name).then((numSet) => {
          if (!numSet || !numSet.value) {
            // default value
            this.set(numberSetting.name, numberSetting.value);
          } else {
            numberSetting.value = +numSet.value;
          }
          SettingsService.settings[numberSetting.name] = numberSetting.value;
          this._settingData.next(SettingsService.settings);
        });
    }
    SettingsService.settings = { ...defaultSettings,
      userId: this.userId,
      collectionStartTime: this.collectionStartTime,
      showSettings: defaultShowSettings,
      calibrationSections,

      completeSoundPath,
      notificationSoundPath,
      serverURL,
      dailyStopTime: new Date(
        1970,
        0,
        1,
        defaultSettings.dafaultStopTimeHour,
        defaultSettings.dafaultStopTimeMinute
      ),

      dailyStartTime: new Date(
        1970,
        0,
        1,
        defaultSettings.dafaultStartTimeHour,
        defaultSettings.dafaultStartTimeMinute
        ),
        networkInterface:
          window.localStorage.getItem('Interface') || 'wifi',
        bluetoothDeviceAddress: window.localStorage.getItem(
          'bluetoothDeviceAddress'
        ),
    };

    this.getObject('calibrationSections').then((sections) => {
      if (!sections || sections.length < 1) {
        this.setObject('calibrationSections', calibrationSections);
        SettingsService.settings.calibrationSections = calibrationSections;
      } else {
        this.setObject('calibrationSections', sections);
        SettingsService.settings.calibrationSections = sections;
      }
    });

  }

  // set a key/value
  async set(key: string, value: any): Promise<any> {
    try {
      //send to servier
      const result = await Preferences.set({ key, value });

      const recordAction = {
        type: 'settingChanged',
        data: { newValue: value, setting: key },
        date: GetDate.getDate(),
      };
      return true;
    } catch (reason) {
      return false;
    }
  }
  // to get a key/value pair
  async get(key: string): Promise<any> {
    try {
      const result = await Preferences.get({ key });
      if (result != null) {
        return result;
      }
      return null;
    } catch (reason) {
      return null;
    }
  }
  // set a key/value object
  async setObject(key: string, object: Object) {
    try {
      const value = JSON.stringify(object);
      const result = await Preferences.set({ key, value });
      return true;
    } catch (reason) {
      return false;
    }
  }


  // get a key/value object
  async getObject(key: string): Promise<any> {
    try {
      const result = await Preferences.get({ key });
      if (result != null) {
        return JSON.parse(result.value);
      }
      return null;
    } catch (reason) {
      return null;
    }
  }
  // remove a single key value:
  async remove(key: string) {
    await Preferences.remove({ key });
  }
  //  delete all data from your application:
  clear() {
    Preferences.clear();
  }

  fetchSettingFromServer() {
    return this.http.get(SettingsService.settings.serverURL + '/settings')
    .pipe(
      take(1),
      timeout(10000), //timeout of 10 seconds
    );
  }

  sendSettingsToServer() {
    const settings = {
      settings_time: GetDate.getDate().toISOString(),
      data: SettingsService.settings,
    };
    return this.http
      .post(SettingsService.settings.serverURL + '/settings', settings)
      .subscribe((resp) => {
      });
  }
}
